import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import PERM from '../../constants/permissions';

const AdminNav = ({ authUser, signOut }) => {
  const location = useLocation();
  const { can, canAny } = usePermission();

  // Per-item, menggantikan flag biner `hasFullAccess` lama (lihat plan RBAC
  // Fase C). Sama seperti AdminSidebarLinks.js -- dua file ini HARUS sinkron
  // soal permission per item, karena keduanya merender menu yang sama.
  const dashboardDisabled = !can(PERM.DASHBOARD_VIEW);
  const registrationsDisabled = !can(PERM.REGISTRATIONS_VIEW);
  const publicationDisabled = !can(PERM.PUBLICATION_VIEW);
  // Sebelumnya TIDAK PERNAH digating sama sekali (bug yang sama dengan
  // AdminSidebarLinks.js) -- RBAC menutup celah ini.
  const recommendationDisabled = !can(PERM.RECOMMENDATION_VIEW);
  const capacityBuildingDisabled = !can(PERM.CAPACITY_BUILDING_VIEW);
  const evaluationDisabled = !can(PERM.EVALUATION_VIEW);
  const classificationDisabled = !can(PERM.CLASSIFICATION_VIEW);
  const usersDisabled = !can(PERM.USERS_VIEW);
  const mentorsDisabled = !can(PERM.MENTORS_VIEW);
  const penggunaHeaderDisabled = !canAny([PERM.USERS_VIEW, PERM.MENTORS_VIEW]);
  const institutionsDisabled = !can(PERM.INSTITUTIONS_VIEW);
  const universitiesDisabled = !can(PERM.UNIVERSITIES_VIEW);

  return (
    <div className='dropdown'>
      <a
        href='#/'
        className='d-inline-flex align-items-center link-dark text-decoration-none dropdown-toggle px-3 py-2 rounded-pill bg-white border shadow-xs transition-all hover-scale'
        data-bs-toggle='dropdown'
        aria-expanded='false'
        style={{ border: '1px solid #e2e8f0' }}
      >
        <div
          className='p-1 bg-danger bg-opacity-10 text-danger rounded-circle me-2 d-flex align-items-center justify-content-center'
          style={{ width: '32px', height: '32px' }}
        >
          <i className='bi bi-shield-lock-fill small'></i>
        </div>
        <div className='text-start me-2'>
          <div
            className='fw-bold text-dark mb-0 leading-tight'
            style={{ fontSize: '0.875rem' }}
          >
            {authUser?.fullName}
          </div>
          <div
            className='text-muted small'
            style={{ fontSize: '0.75rem' }}
          >
            {authUser?.activeSession?.roleName || 'Administrator'}
          </div>
        </div>
      </a>

      <ul
        className='dropdown-menu dropdown-menu-lg-end shadow-lg border-0 rounded-4 p-2 mt-2 mt-lg-3'
        style={{
          minWidth: '240px',
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <li className='px-3 py-2 mb-2 border-bottom border-light'>
          <h6 className='fw-bold text-dark mb-0'>
            {authUser?.fullName}
          </h6>
          <small className='text-muted'>{authUser?.email}</small>
          <div className='mt-2'>
            <span className='badge bg-danger bg-opacity-10 text-danger rounded-pill x-small'>
              Admin
            </span>
          </div>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${dashboardDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => dashboardDisabled && e.preventDefault()}
            to={dashboardDisabled ? '#' : '/admin'}
            end
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-speedometer2 ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Dashboard</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${registrationsDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => registrationsDisabled && e.preventDefault()}
            to={registrationsDisabled ? '#' : '/admin/registrations'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-box-arrow-in-right ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Pendaftaran</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${publicationDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => publicationDisabled && e.preventDefault()}
            to={publicationDisabled ? '#' : '/admin/publication'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-envelope-paper-fill ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Publikasi</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${recommendationDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => recommendationDisabled && e.preventDefault()}
            to={recommendationDisabled ? '#' : '/admin/recommendation-letter'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-send-arrow-down-fill ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Surat Rekomendasi</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${capacityBuildingDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => capacityBuildingDisabled && e.preventDefault()}
            to={capacityBuildingDisabled ? '#' : '/admin/capacity-building'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-smartwatch ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Capacity Building</span>
              </>
            )}
          </NavLink>
        </li>

        <li className={`dropdown-header small text-uppercase text-muted fw-bold pt-2 pb-1 ${evaluationDisabled && classificationDisabled ? 'opacity-50' : ''}`}>
          Penilaian
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ps-4 ${evaluationDisabled ? 'text-muted opacity-50 pe-none' : ''} ${isActive || location.pathname.includes('/admin/students') ? 'active' : ''}`
            }
            onClick={(e) => evaluationDisabled && e.preventDefault()}
            to={evaluationDisabled ? '#' : '/admin/students/evaluations'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-people-fill ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Mahasiswa</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ps-4 ${classificationDisabled ? 'text-muted opacity-50 pe-none' : ''} ${isActive || location.pathname.includes('/admin/classifications') ? 'active' : ''}`
            }
            onClick={(e) => classificationDisabled && e.preventDefault()}
            to={classificationDisabled ? '#' : '/admin/classifications'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-hand-thumbs-up-fill ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>KPI</span>
              </>
            )}
          </NavLink>
        </li>

        <li className={`dropdown-header small text-uppercase text-muted fw-bold pt-2 pb-1 ${penggunaHeaderDisabled ? 'opacity-50' : ''}`}>
          Pengguna
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ps-4 ${usersDisabled ? 'text-muted opacity-50 pe-none' : ''} ${isActive || location.pathname.includes('/admin/users') ? 'active' : ''}`
            }
            onClick={(e) => usersDisabled && e.preventDefault()}
            to={usersDisabled ? '#' : '/admin/users'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-people ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Mahasiswa</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ps-4 ${mentorsDisabled ? 'text-muted opacity-50 pe-none' : ''} ${isActive || location.pathname.includes('/admin/mentors') ? 'active' : ''}`
            }
            onClick={(e) => mentorsDisabled && e.preventDefault()}
            to={mentorsDisabled ? '#' : '/admin/mentors'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-person-badge ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Mentor</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${institutionsDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => institutionsDisabled && e.preventDefault()}
            to={institutionsDisabled ? '#' : '/admin/institutions'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-building ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Lembaga</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <NavLink
            className={({ isActive }) =>
              `dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 ${universitiesDisabled ? 'text-muted opacity-50 pe-none' : (isActive ? 'active' : '')}`
            }
            onClick={(e) => universitiesDisabled && e.preventDefault()}
            to={universitiesDisabled ? '#' : '/admin/universities'}
          >
            {({ isActive }) => (
              <>
                <i
                  className={`bi bi-building-fill-check ${isActive ? 'text-white' : 'text-danger opacity-75'}`}
                ></i>
                <span>Universitas</span>
              </>
            )}
          </NavLink>
        </li>

        <li>
          <hr className='dropdown-divider opacity-50' />
        </li>

        <li>
          <a
            onClick={(e) => {
              e.preventDefault();
              signOut();
            }}
            className='dropdown-item d-flex align-items-center gap-2 py-2 rounded-3 text-danger'
            href='#/'
          >
            <i className='bi bi-box-arrow-right'></i>
            <span className='fw-bold'>Sign out</span>
          </a>
        </li>
      </ul>
    </div>
  );
};

export default AdminNav;
